import type { Guide } from "./guides";

/**
 * English list guides, the counterparts of the Spanish /es/guias pages.
 *
 * US demand for this space is mostly list intent, far above the app-intent
 * queries the Sep 2026 audit measured (~700/mo): "movies to watch with family"
 * and "family movies to watch" 14.8k/mo each, "movies to watch with boyfriend"
 * 5.4k, "movies to watch with friends" 4.4k, "movie night ideas" 2.9k (OpenSEO,
 * 25 Sep 2026). Each guide pairs well-known titles with the choose-together
 * method. Never claim a title is on a specific service: catalogs change.
 */
export const listGuidesEn: Guide[] = [
  {
    slug: "movies-to-watch-as-a-couple",
    title: "Movies to watch with your boyfriend or girlfriend (and how to pick one you'll both enjoy)",
    metaTitle: "Movies to Watch With Your Boyfriend or Girlfriend: 20 Picks",
    description:
      "20 movies to watch with your boyfriend or girlfriend, grouped by the kind of night you want, plus a five-minute way for couples to pick one without arguing.",
    published: "2026-09-25",
    updated: "2026-09-29",
    answer:
      "To pick a movie with your boyfriend or girlfriend, each of you writes down three titles you'd actually watch tonight, you only choose from what overlaps, and you decide by mood rather than genre. Below are 20 picks for couples, grouped by the kind of night you want, and not all of them are romances.",
    intro: [
      "The problem is rarely that there's nothing to watch. It's that there's too much, and every suggestion starts a small negotiation: \"seen it\", \"too heavy for tonight\", \"what about something else?\". Forty minutes later you're still scrolling.",
      "This guide has two parts: a quick way to decide together, and a list of movies grouped by the kind of night you're after. They work just as well whether you're watching with a boyfriend, a girlfriend, a husband or a wife.",
    ],
    sections: [
      {
        heading: "How to pick in five minutes",
        ordered: true,
        list: [
          "Each of you writes down three movies or shows you'd actually watch tonight, without showing the other.",
          "Compare lists. If anything appears on both, that's your pick.",
          "If nothing overlaps, each person crosses one title off the other's list, no explanation needed.",
          "From what's left, choose by mood (funny, tense, easy) rather than genre.",
          "Still tied? Whoever didn't choose last time decides.",
        ],
      },
      {
        heading: "To laugh together",
        list: [
          "Crazy, Stupid, Love (2011): a sharp comedy with a great cast and a twist you won't see coming.",
          "Palm Springs (2020): two people stuck in the same repeating day. Light and genuinely original.",
          "Little Miss Sunshine (2006): a disastrous family road trip that's funny and warm in equal measure.",
        ],
      },
      {
        heading: "Thrillers you'll both get hooked on",
        list: [
          "Gone Girl (2014): a thriller about a marriage that gives you plenty to talk about afterward.",
          "Knives Out (2019): a classic whodunit with a lot of humor. Great for guessing together.",
          "Parasite (2019): starts as a comedy and turns into something else. Best watched knowing nothing.",
        ],
      },
      {
        heading: "Romance that isn't too sweet",
        list: [
          "Before Sunrise (1995): two strangers, one night in Vienna, and nothing but conversation.",
          "La La Land (2016): a modern musical with an ending you'll both have opinions about.",
          "Past Lives (2023): quiet, beautiful and easy to get lost in.",
        ],
      },
      {
        heading: "Adventure and sci-fi",
        list: [
          "Interstellar (2014): a space epic with real emotional pull. Save it for a night with no rush.",
          "Mad Max: Fury Road (2015): two hours of action with barely a pause.",
          "Dune (2021): sci-fi on a huge scale, and there's a sequel if you're hooked.",
        ],
      },
      {
        heading: "Animation that works for adults",
        list: [
          "Inside Out (2015): funny, and much deeper than it looks.",
          "Spider-Man: Into the Spider-Verse (2018): visually unlike anything else, even if superheroes aren't your thing.",
          "Spirited Away (2001): the Studio Ghibli classic worth seeing at least once.",
        ],
      },
      {
        heading: "Feel-good picks for a cozy night in",
        list: [
          "About Time (2013): a warm, funny story about family and making the most of ordinary days.",
          "The Proposal (2009): a light comedy that works even if only one of you loves rom-coms.",
          "Chef (2014): food, road trips and a feel-good ending. Don't watch it hungry.",
          "The Grand Budapest Hotel (2014): a quick, stylish caper with a lot of charm.",
          "Groundhog Day (1993): a comedy classic that both of you will quote afterward.",
        ],
      },
      {
        heading: "Shows to watch as a couple",
        paragraphs: [
          "If you'd rather have something for several nights, a series saves you deciding every time. A few that tend to work across different tastes: The Office (2005), a comedy in short episodes; Ted Lasso (2020), upbeat and funny; Stranger Things (2016), adventure with 80s nostalgia; and Dark (2017), for anyone who likes piecing together a puzzle.",
          "Where each title is streaming changes by country and service, so check before you settle on one.",
        ],
      },
      {
        heading: "When your tastes are opposites",
        paragraphs: [
          "There's almost always overlap at the edges. Someone who loves horror and someone who can't stand it will usually both enjoy a good thriller. Someone who wants action and someone who wants drama tend to meet in a heist movie or a mystery.",
          "Taking turns works too: one night each of you picks, with the single rule that you can't choose something the other has already said they hate.",
        ],
      },
      {
        heading: "A movie picker for couples",
        paragraphs: [
          "ReelMatch automates the two-list method. You each swipe through trailers on your own phone whenever you have a spare minute: say yes to what you'd watch and skip the rest. When you both say yes to the same title, it shows up in your list of matches.",
          "Because the deciding happens ahead of time, movie night is just picking from a list you've both already approved. ReelMatch is free on iPhone and Android.",
        ],
      },
    ],
    faqs: [
      {
        question: "What's a good movie to watch as a couple that isn't a romance?",
        answer:
          "A good mystery or comedy usually works better than a romance when your tastes differ. Knives Out (2019), Parasite (2019) and Palm Springs (2020) are picks most people enjoy, and they give you something to talk about afterward.",
      },
      {
        question: "What movies are good to watch with a boyfriend or girlfriend?",
        answer:
          "Pick by the mood you both want rather than the genre. For a light night, try Crazy, Stupid, Love (2011) or Palm Springs (2020). For something gripping, Gone Girl (2014) or Knives Out (2019). If you can't agree, each write down three options and choose from what overlaps.",
      },
      {
        question: "How do we choose when our tastes are completely different?",
        answer:
          "Each suggest three titles separately and keep the ones that overlap. If nothing does, each cross off one of the other's picks and decide by mood instead of genre. Thrillers and mysteries are usually common ground.",
      },
      {
        question: "Is there a movie picker app for couples?",
        answer:
          "Yes. ReelMatch is a free movie picker for couples, friends and families on iPhone and Android. You each swipe through trailers on your own phone, and it shows only the titles you both said yes to.",
      },
    ],
    related: [
      "feel-good-movies",
      "how-to-decide-what-to-watch-with-your-partner",
      "movies-to-watch-with-friends",
      "family-movies-to-watch",
    ],
  },

  {
    slug: "family-movies-to-watch",
    title: "Family movies to watch: picks by age, and how to choose without a fight",
    metaTitle: "Family Movies to Watch: Picks by Age and How to Choose",
    description:
      "Family movies to watch, organized by age from little kids to teens, plus a simple way to choose one the whole family will enjoy without an argument.",
    published: "2026-09-25",
    updated: "2026-10-14",
    answer:
      "To choose a family movie, start from the youngest viewer's age, let everyone suggest one title, and pick the one nobody vetoes. Below are 16 family movies organized by age, plus classics that work for everyone.",
    intro: [
      "Choosing a family movie has an extra layer of difficulty: it's not just different tastes, it's different ages. What keeps a six-year-old happy bores a fourteen-year-old, and what the fourteen-year-old wants may not be right for the six-year-old.",
      "The good news is that plenty of movies work for everyone. Here's how to choose, and a list to start family movie night with.",
    ],
    sections: [
      {
        heading: "How to choose without anyone getting upset",
        ordered: true,
        list: [
          "Start from the youngest viewer's age. That decides which options are on the table.",
          "Everyone suggests one movie, kids included.",
          "Everyone gets one veto, no explanation needed.",
          "If more than one is left, pick the shortest. A 90-minute movie usually ends better than a three-hour one.",
          "Keep track of who chose, so next time it's someone else's turn.",
        ],
      },
      {
        heading: "For little kids",
        list: [
          "Toy Story (1995): the Pixar classic that still works at every age.",
          "Coco (2017): music, color and a story that moves kids and adults alike.",
          "My Neighbor Totoro (1988): gentle and magical, ideal for young children.",
          "Finding Nemo (2003): adventure, humor and an ending that leaves everyone happy.",
        ],
      },
      {
        heading: "Ages 8 to 12",
        list: [
          "Paddington 2 (2017): funny and kind, with humor adults enjoy just as much.",
          "Encanto (2021): a musical with songs that stick.",
          "How to Train Your Dragon (2010): dragons, adventure and a lot of heart.",
          "Harry Potter and the Sorcerer's Stone (2001): the perfect start to a family series.",
        ],
      },
      {
        heading: "With teens",
        list: [
          "Back to the Future (1985): a sci-fi comedy that never gets old.",
          "Jurassic Park (1993): adventure with real suspense. Some scenes may scare younger kids.",
          "The Hunger Games (2012): action and tension for teens and adults.",
          "Spider-Man: Into the Spider-Verse (2018): animation with a style teens love.",
        ],
      },
      {
        heading: "Classics that work for everyone",
        list: [
          "Up (2009): the first ten minutes get to everyone.",
          "The Incredibles (2004): superheroes, humor and a very recognizable family.",
          "WALL-E (2008): almost no dialogue at first, so it works even for the youngest.",
          "Inside Out (2015): fun for kids and eye-opening for adults.",
        ],
      },
      {
        heading: "Family movie night ideas",
        list: [
          "Pick the movie before dinner, not once everyone is on the couch.",
          "Pair the movie with a snack that fits it, like popcorn for a classic or tacos for Coco.",
          "Let the kids hand out the vetoes and keep score of who chose last.",
          "Keep a running list of movies everyone wants to see, so next time starts with options.",
        ],
      },
      {
        heading: "Check the age rating",
        paragraphs: [
          "Before choosing something for kids, check the rating and, if you're unsure, watch the trailer first. Two minutes of trailer tells you a lot about the tone and whether anything might scare them.",
        ],
      },
      {
        heading: "Finding a TV show the whole family will enjoy",
        paragraphs: [
          "Shows work the same way, with one extra step. Set the age floor from the youngest viewer, pick a shared genre (comedy, adventure, mystery and animation work best for mixed ages), then try a two-episode test: watch the first two episodes together, and if nobody is checking their phone by the end, you've found your family show.",
          "Shows that tend to work for kids, teens and adults together include Bluey, Avatar: The Last Airbender, The Mandalorian and Stranger Things for families with older kids. Check the rating for your youngest viewer first.",
        ],
      },
      {
        heading: "Choosing as a family with ReelMatch",
        paragraphs: [
          "In ReelMatch, each person swipes through trailers on their own phone and the app shows the titles everyone said yes to. It works with groups of three or more, so it suits families where several people have a phone.",
          "Seeing the trailer before deciding helps a lot with kids: you can tell quickly whether something will scare or bore them. ReelMatch is free on iPhone and Android.",
        ],
      },
    ],
    faqs: [
      {
        question: "What are good family movies to watch with young kids?",
        answer:
          "Animated movies with simple stories and little suspense are the safest bet: Toy Story (1995), Finding Nemo (2003) and My Neighbor Totoro (1988) work from a very young age and keep adults entertained too.",
      },
      {
        question: "How do I know if a movie is right for my kids?",
        answer:
          "Check the age rating, which usually appears on the service you're watching on, and watch the trailer first. A two-minute trailer shows the tone well and whether there are scenes that might scare them.",
      },
      {
        question: "What are some great movies to watch with the whole family?",
        answer:
          "Classics that work for every age include The Princess Bride (1987), Back to the Future (1985), Paddington 2 (2017) and The Incredibles (2004). For older kids, see our guide to good movies to watch with your teen.",
      },
      {
        question: "What are some family movie night ideas?",
        answer:
          "Choose the movie before dinner, give everyone one suggestion and one veto, match a snack to the movie, and keep a running list of movies everyone wants to see so the next movie night starts with options.",
      },
      {
        question: "Can ReelMatch help a family choose a movie?",
        answer:
          "Yes, if several people in the family have a phone. Each person swipes through trailers on their own phone and the app shows the titles everyone said yes to. It's free on iPhone and Android.",
      },
    ],
    related: [
      "family-christmas-movies",
      "movies-to-watch-with-teens",
      "movies-to-watch-with-your-mom",
      "movies-to-watch-with-friends",
      "movies-to-watch-as-a-couple",
      "how-to-pick-a-movie-for-a-group",
    ],
  },

  {
    slug: "movies-to-watch-with-friends",
    title: "Movies to watch with friends: movie night ideas by genre",
    metaTitle: "Movies to Watch With Friends: Movie Night Ideas by Genre",
    description:
      "How to pick movies to watch with friends without an endless debate, plus 15 movie night ideas by genre, from comedies and horror to mysteries.",
    published: "2026-09-25",
    updated: "2026-09-25",
    answer:
      "To pick a movie with friends, don't open the decision to everyone at once: collect suggestions privately, build a shortlist nobody objects to, and vote from there. Below are 15 movies to watch with friends, from comedies to horror.",
    intro: [
      "Picking between two people is hard enough. With four or five, the debate can outlast the movie. It's not really about taste: the more people weigh in out loud, the easier it is for someone to say no.",
      "This guide covers how to decide quickly as a group, with ideas for different kinds of movie night.",
    ],
    sections: [
      {
        heading: "How to pick as a group without the endless debate",
        ordered: true,
        list: [
          "Before you meet up, everyone messages three titles they'd actually watch.",
          "One person collects them and removes duplicates. Anything suggested twice is already a favorite.",
          "Everyone gets one veto, used privately.",
          "Vote on what's left. If it's a tie, pick at random.",
          "Agree on a maximum length before you start. On a weeknight, under two hours is safer.",
        ],
      },
      {
        heading: "Funny movies to watch with friends",
        list: [
          "Superbad (2007): the definitive friends comedy.",
          "The Hangover (2009): a bachelor party that goes very wrong. Better in a group.",
          "Bridesmaids (2011): a comedy with scenes people quote for years.",
          "Wild Tales (2014): six darkly funny Argentine stories. Great for talking between segments.",
        ],
      },
      {
        heading: "Horror movies to watch with friends",
        list: [
          "Get Out (2017): horror with social commentary that sparks a lot of conversation afterward.",
          "The Conjuring (2013): classic scares, perfect for a group with the lights off.",
          "A Quiet Place (2018): constant tension. Even the group goes quiet.",
        ],
      },
      {
        heading: "Non-stop action",
        list: [
          "John Wick (2014): straightforward action, and several sequels if it lands.",
          "Mission: Impossible – Fallout (2018): some of the most spectacular action scenes around.",
          "Mad Max: Fury Road (2015): two hours of chase that nobody gets bored of.",
        ],
      },
      {
        heading: "Mysteries to solve together",
        list: [
          "Knives Out (2019): perfect for betting on who did it.",
          "Nine Queens (2000): con artists in Buenos Aires and an ending that surprises.",
          "The Invisible Guest (2016): a Spanish thriller full of twists. Don't read anything first.",
          "Gone Girl (2014): everyone comes away with their own theory.",
          "Parasite (2019): works just as well with a group as with two people.",
        ],
      },
      {
        heading: "Movie night ideas",
        list: [
          "Pick the movie before everyone arrives, not once you're all on the couch.",
          "Check where it's streaming and that someone has the account.",
          "Set a start time and stick to it. Latecomers can catch up.",
          "Get the snacks ready before you press play so you're not pausing every ten minutes.",
          "Try a theme night: a double feature, a director's best, or one movie from each person's childhood.",
        ],
      },
      {
        heading: "Picking as a group with ReelMatch",
        paragraphs: [
          "ReelMatch runs the shortlist method for you. During the week, everyone swipes through trailers on their own phone and the app keeps only the titles the whole group said yes to. By movie night, the list is ready.",
          "It works with groups of three or more, and iPhone and Android users can use it together. It's free.",
        ],
      },
    ],
    faqs: [
      {
        question: "What's a good movie to watch with friends?",
        answer:
          "It depends on the night, but comedies and mysteries tend to work best in a group because everyone gets involved: laughing together or trying to guess the ending. Superbad (2007), Knives Out (2019) and Wild Tales (2014) are safe bets.",
      },
      {
        question: "What are good horror movies to watch with friends?",
        answer:
          "Pick horror that's fun to react to together. Get Out (2017) gives you plenty to talk about afterward, The Conjuring (2013) delivers classic jump scares, and A Quiet Place (2018) keeps the whole room tense.",
      },
      {
        question: "How do you choose a movie with a big group?",
        answer:
          "Collect suggestions privately before you meet, build a shortlist with no duplicates, give each person one veto and vote on what's left. Nobody has to say no out loud, and the decision takes minutes.",
      },
      {
        question: "Is there an app for choosing a movie as a group?",
        answer:
          "Yes. In ReelMatch everyone swipes through trailers on their own phone and the app shows the titles the whole group said yes to. It's free on iPhone and Android.",
      },
    ],
    related: [
      "scary-movies-to-watch-with-friends",
      "how-to-pick-a-movie-for-a-group",
      "movies-to-watch-as-a-couple",
      "family-movies-to-watch",
    ],
  },
  {
    slug: "movies-to-watch-with-teens",
    title: "Good movies to watch with your teen: picks they won't roll their eyes at",
    metaTitle: "Good Movies for Teens to Watch With the Family",
    description:
      "20 good movies for teens that parents enjoy too, from 80s classics to modern favorites, with US ratings and tips for picking one together.",
    published: "2026-09-29",
    updated: "2026-09-29",
    answer:
      "The best movies to watch with a teenager are ones they'd pick themselves: sharp comedies, coming-of-age stories and smart sci-fi, not \"family movies\". Let your teen suggest or veto first, check the rating together, and use the trailer to settle it. Below are 20 picks with their US ratings.",
    intro: [
      "Watching a movie with a teenager is a small win. The hard part is finding something that doesn't feel too young to them or too much for you, and that doesn't start a debate before anyone presses play.",
      "The trick is to let them lead. Teens are far more invested in a movie they helped choose. The list below mixes classics you'll enjoy rewatching with newer films they may already be curious about.",
    ],
    sections: [
      {
        heading: "How to pick a movie with your teen",
        ordered: true,
        list: [
          "Let your teen suggest three titles first, and add one of your own.",
          "Each of you gets one veto, no explanation needed.",
          "Check the rating and watch the trailer together. It's a quick, low-pressure way to agree on tone.",
          "If you're still stuck, pick the shortest one. You can always watch another next week.",
        ],
      },
      {
        heading: "Classics that still land",
        list: [
          "The Princess Bride (1987, PG): adventure, romance and comedy that every generation seems to love.",
          "Back to the Future (1985, PG): time travel, great jokes and a soundtrack they'll recognize.",
          "Ferris Bueller's Day Off (1986, PG-13): the ultimate skip-school fantasy, still funny.",
          "The Breakfast Club (1985, R): five very different teens stuck in detention. Best for older teens.",
        ],
      },
      {
        heading: "Comedies teens actually like",
        list: [
          "Clueless (1995, PG-13): sharp, quotable and surprisingly smart.",
          "10 Things I Hate About You (1999, PG-13): a high-school take on Shakespeare that holds up.",
          "Mean Girls (2004, PG-13): a comedy about high-school cliques that most teens can quote.",
          "School of Rock (2003, PG-13): pure fun, especially for any teen who plays music.",
        ],
      },
      {
        heading: "Coming-of-age stories worth talking about",
        list: [
          "Juno (2007, PG-13): funny and honest, with a lot to talk about afterward.",
          "The Perks of Being a Wallflower (2012, PG-13): friendship, belonging and finding your people.",
          "Lady Bird (2017, R): a mother-daughter story that parents and teens see from different sides.",
          "Hidden Figures (2016, PG): the true story of the women whose math got astronauts into space.",
        ],
      },
      {
        heading: "Sci-fi, adventure and action",
        list: [
          "The Martian (2015, PG-13): a stranded astronaut solves problems with science and humor.",
          "Dune (2021, PG-13): big-screen sci-fi for teens who like epic worlds.",
          "Jumanji: Welcome to the Jungle (2017, PG-13): a fun adventure comedy that works for mixed ages.",
          "Spider-Man: Across the Spider-Verse (2023, PG): stunning animation that teens rate highly.",
        ],
      },
      {
        heading: "A little scary, not too scary",
        list: [
          "A Quiet Place (2018, PG-13): tense rather than gory, and great for a family watching together.",
          "Coraline (2009, PG): creepy and beautifully made. A good first scary movie for younger teens.",
          "Holes (2003, PG): a mystery adventure with just enough edge.",
          "The Hunger Games (2012, PG-13): intense but not graphic, and a good way into a longer series.",
        ],
      },
      {
        heading: "Check the rating, then the trailer",
        paragraphs: [
          "Ratings above are US ratings and can differ in other countries. A rating tells you roughly what's in a movie, but a two-minute trailer tells you much more about the tone. Watching it together also makes the choice feel shared rather than imposed.",
        ],
      },
      {
        heading: "Choosing together with ReelMatch",
        paragraphs: [
          "In ReelMatch, each person swipes through trailers on their own phone and the app shows the titles everyone said yes to. Teens tend to like it because they get a real say without having to argue for it. It's free on iPhone and Android.",
        ],
      },
    ],
    faqs: [
      {
        question: "What are good movies to watch with teenagers?",
        answer:
          "Sharp comedies, coming-of-age stories and smart sci-fi tend to work best: Clueless (1995), The Princess Bride (1987), The Martian (2015) and Spider-Man: Across the Spider-Verse (2023) are safe bets that parents enjoy too.",
      },
      {
        question: "What are good PG-13 movies for teens?",
        answer:
          "Mean Girls (2004), School of Rock (2003), Clueless (1995), The Martian (2015) and A Quiet Place (2018) are all rated PG-13 and popular with teens and parents alike.",
      },
      {
        question: "How do I get my teen to watch a movie with me?",
        answer:
          "Let them choose. Ask for three suggestions, add one of your own, give each of you one veto and settle it with the trailers. Teens are much more likely to join in when the pick was partly theirs.",
      },
      {
        question: "Is there an app to help families pick a movie?",
        answer:
          "Yes. In ReelMatch each person swipes through trailers on their own phone and the app shows the titles everyone said yes to. It's free on iPhone and Android.",
      },
    ],
    related: [
      "family-movies-to-watch",
      "scary-movies-to-watch-with-friends",
      "movies-to-watch-with-your-mom",
    ],
  },

  {
    slug: "scary-movies-to-watch-with-friends",
    title: "Scary movies to watch with friends this Halloween: from fun frights to real nightmares",
    metaTitle: "Best Scary Movies to Watch With Friends (Halloween 2026)",
    description:
      "24 of the best scary and horror movies to watch with friends this Halloween, from fun horror-comedy to truly terrifying, plus how to choose as a group.",
    published: "2026-09-29",
    updated: "2026-10-08",
    answer:
      "The best scary movies to watch with friends are ones that are fun to react to together: horror-comedies like Scream or Shaun of the Dead, tense crowd-pleasers like A Quiet Place, and smart horror like Get Out. Agree on a scare level first so nobody spends the night behind a cushion. Below are 24 picks for Halloween 2026, sorted by how scary they are.",
    intro: [
      "Horror is the best genre to watch in a group. The jump scares are funnier, the tension is bigger and everyone has something to say afterward.",
      "The one thing to get right is the scare level. A group with one horror fan and three nervous friends needs a different movie than a room full of people who've seen everything. The list below is sorted from fun frights to genuinely terrifying.",
    ],
    sections: [
      {
        heading: "Pick a scare level first",
        ordered: true,
        list: [
          "Ask the group how scary they want it: fun, tense or terrifying.",
          "Everyone suggests titles privately, then remove anything above the agreed level.",
          "Each person gets one veto.",
          "Watch the trailers for the last two or three. That settles it faster than any debate.",
        ],
      },
      {
        heading: "Fun frights: horror-comedies",
        list: [
          "Scream (1996, R): a slasher that knows all the horror rules and plays with them.",
          "Shaun of the Dead (2004, R): a zombie outbreak meets a very British comedy.",
          "The Cabin in the Woods (2012, R): starts like a classic cabin horror, then turns into something else entirely.",
          "Happy Death Day (2017, PG-13): a time-loop slasher that's more fun than frightening.",
          "Ready or Not (2019, R): a deadly game of hide-and-seek with a dark sense of humor.",
        ],
      },
      {
        heading: "Tense crowd-pleasers",
        list: [
          "A Quiet Place (2018, PG-13): the whole room will go silent with the characters.",
          "The Conjuring (2013, R): classic haunted-house scares done extremely well.",
          "It (2017, R): a coming-of-age story with a very scary clown.",
          "Us (2019, R): a family vacation that goes deeply, strangely wrong.",
        ],
      },
      {
        heading: "Smart horror to talk about afterward",
        list: [
          "Get Out (2017, R): horror with social commentary that sparks a lot of conversation.",
          "Barbarian (2022, R): a rental-house booking gone wrong, with twists you won't predict.",
          "Hereditary (2018, R): slow, disturbing and unforgettable. Only for a group that wants to be truly scared.",
        ],
      },
      {
        heading: "Classics every horror night needs",
        list: [
          "Halloween (1978, R): the original slasher, still tense.",
          "The Thing (1982, R): paranoia in the Antarctic, with legendary practical effects.",
          "The Shining (1980, R): slow-building dread in an empty hotel.",
        ],
      },
      {
        heading: "Newer horror your group may not have seen",
        list: [
          "M3GAN (2022, PG-13): a killer-doll movie that's as funny as it is creepy. Great for a mixed group.",
          "Nope (2022, R): Jordan Peele's big-sky mystery. Tense, strange and fun to argue about afterward.",
          "Smile (2022, R): a curse that spreads from person to person. Lots of jump scares.",
          "Talk to Me (2023, R): teenagers play a party game with a cursed hand. Genuinely frightening.",
        ],
      },
      {
        heading: "Family-friendly Halloween picks",
        list: [
          "Hocus Pocus (1993, PG): a Halloween favorite for all ages.",
          "Beetlejuice (1988, PG): spooky, silly and very quotable.",
          "The Nightmare Before Christmas (1993, PG): works for both Halloween and December.",
          "Coraline (2009, PG): creepy enough to feel grown-up, gentle enough for younger kids.",
          "Monster House (2006, PG): a haunted-house adventure made for a first scary movie.",
        ],
      },
      {
        heading: "Set up the night",
        list: [
          "Lights off, phones away, snacks ready before you press play.",
          "Agree on a pause rule in advance, so nobody stops the movie at the scariest moment.",
          "Double features work well: start with a horror-comedy, then go darker.",
        ],
      },
      {
        heading: "Picking as a group with ReelMatch",
        paragraphs: [
          "In ReelMatch everyone swipes through trailers on their own phone, and the app keeps only the titles the whole group said yes to. That's an easy way to find the scary movie everyone's actually up for. It's free on iPhone and Android.",
        ],
      },
    ],
    faqs: [
      {
        question: "What's a good scary movie to watch with friends?",
        answer:
          "Scream (1996) and Shaun of the Dead (2004) are the safest picks because they're scary and funny. For something tenser, A Quiet Place (2018) or The Conjuring (2013) work well with a group.",
      },
      {
        question: "What horror movie should we watch if some of us don't like horror?",
        answer:
          "Choose a horror-comedy such as Happy Death Day (2017) or Shaun of the Dead (2004), or a tense PG-13 movie like A Quiet Place (2018). Agree on a scare level before you start.",
      },
      {
        question: "What are good Halloween movies to watch with family?",
        answer:
          "Hocus Pocus (1993), Beetlejuice (1988), The Nightmare Before Christmas (1993), Coraline (2009) and Monster House (2006) are all rated PG and work for mixed ages.",
      },
      {
        question: "What's the scariest movie to watch with friends?",
        answer:
          "For a group that wants to be truly scared, Hereditary (2018), The Conjuring (2013), Talk to Me (2023) and Barbarian (2022) are among the most frightening picks on this list.",
      },
      {
        question: "What should we watch at a Halloween party?",
        answer:
          "Horror-comedies work best when people are chatting: Scream (1996), Shaun of the Dead (2004) or M3GAN (2022). With kids around, Hocus Pocus (1993) and Beetlejuice (1988) are safe bets. For a double feature, start funny and go darker.",
      },
      {
        question: "How do we choose a scary movie as a group?",
        answer:
          "Agree on how scary you want it, collect suggestions privately, give everyone one veto and watch the trailers of the last few options. In ReelMatch the group swipes through trailers and the app keeps only what everyone said yes to.",
      },
    ],
    related: [
      "movies-to-watch-with-friends",
      "movies-to-watch-with-teens",
      "how-to-pick-a-movie-for-a-group",
    ],
  },

  {
    slug: "movies-to-watch-with-your-mom",
    title: "Good movies to watch with your mom: cute, funny and feel-good picks",
    metaTitle: "Best Movies to Watch With Your Mom: 20 Picks She'll Love",
    description:
      "The 20 best movies to watch with your mom: feel-good comedies, mother-daughter favorites and moving dramas, plus picks for mom and dad together.",
    published: "2026-09-29",
    updated: "2026-10-05",
    answer:
      "Good movies to watch with your mom are ones that are easy to enjoy together: feel-good comedies like The Devil Wears Prada or Mamma Mia!, heartfelt dramas like Little Women, and classics like The Princess Bride. Pick by mood, keep it under two hours, and let each person veto one option. Below are 20 picks, including some for dads and both parents.",
    intro: [
      "A movie with your mom or your parents is one of the easiest ways to spend time together, whether you're home for the holidays or catching up on a quiet evening. The hard part is agreeing on something that suits different generations.",
      "The picks below lean toward movies that work across ages: warm, funny or moving, without anything that makes a family viewing awkward.",
    ],
    sections: [
      {
        heading: "Feel-good comedies",
        list: [
          "The Devil Wears Prada (2006): sharp, funny and stylish. A favorite for moms and daughters.",
          "Mamma Mia! (2008): ABBA songs, a Greek island and a lot of fun.",
          "The Intern (2015): a warm comedy about generations learning from each other.",
          "Legally Blonde (2001): endlessly rewatchable and quotable.",
          "Freaky Friday (2003): a mother and daughter swap bodies. Perfect for watching together.",
        ],
      },
      {
        heading: "Heartfelt dramas",
        list: [
          "Little Women (2019): sisters, ambition and family, beautifully told.",
          "Lady Bird (2017): a mother-daughter story that parents and kids see from different sides.",
          "Steel Magnolias (1989): friendship among women in a small Southern town. Bring tissues.",
          "Hidden Figures (2016): the true story of the women who helped get astronauts into space.",
        ],
      },
      {
        heading: "Easy crowd-pleasers",
        list: [
          "Julie & Julia (2009): two women, one cookbook and a lot of butter.",
          "Crazy Rich Asians (2018): a romantic comedy with a big family at its heart.",
          "The Holiday (2006): a cozy favorite, especially in winter.",
          "Paddington 2 (2017): charming enough for every generation.",
        ],
      },
      {
        heading: "Something a bit different",
        list: [
          "Everything Everywhere All at Once (2022): a wild, funny and moving film about a mother and daughter.",
          "Knives Out (2019): a family whodunit that's fun to solve together.",
          "The Princess Bride (1987): a classic most parents love sharing.",
        ],
      },
      {
        heading: "Movies to watch with your dad or both parents",
        list: [
          "Back to the Future (1985): a crowd-pleaser across three generations.",
          "Apollo 13 (1995): tense and inspiring, based on a true story.",
          "Field of Dreams (1989): a movie about fathers and sons that gets to people.",
          "Top Gun: Maverick (2022): big, fun and a hit with parents.",
        ],
      },
      {
        heading: "How to pick in two minutes",
        ordered: true,
        list: [
          "Decide the mood: laugh, cry or something gripping.",
          "Each person suggests one title, then everyone gets one veto.",
          "If you're tied, choose the shortest one.",
        ],
      },
      {
        heading: "Picking together with ReelMatch",
        paragraphs: [
          "In ReelMatch each person swipes through trailers on their own phone, and the app shows the titles you all said yes to. It works well across generations because everyone gets a say without a long debate. It's free on iPhone and Android.",
        ],
      },
    ],
    faqs: [
      {
        question: "What are good movies to watch with your mom?",
        answer:
          "Feel-good comedies and heartfelt dramas work best: The Devil Wears Prada (2006), Mamma Mia! (2008), Little Women (2019) and Freaky Friday (2003) are popular picks for watching together.",
      },
      {
        question: "What movies are good to watch with your parents?",
        answer:
          "Choose something that works across generations, such as Back to the Future (1985), Apollo 13 (1995), Knives Out (2019) or Paddington 2 (2017).",
      },
      {
        question: "What's a good mother-daughter movie?",
        answer:
          "Lady Bird (2017), Freaky Friday (2003), Everything Everywhere All at Once (2022) and Little Women (2019) all focus on mothers, daughters or sisters, and give you plenty to talk about afterward.",
      },
      {
        question: "Is there an app to help families choose a movie?",
        answer:
          "Yes. In ReelMatch each person swipes through trailers on their own phone and the app shows the titles everyone said yes to. It's free on iPhone and Android.",
      },
    ],
    related: [
      "family-movies-to-watch",
      "movies-to-watch-as-a-couple",
      "movies-to-watch-with-teens",
    ],
  },
  {
    slug: "feel-good-movies",
    title: "Feel-good movies: 25 happy, comfort movies to watch when you need a lift",
    metaTitle: "Feel Good Movies: 25 Happy Comfort Movies to Watch",
    description:
      "25 feel-good movies to watch when you need a lift: happy comedies, comfort rewatches and uplifting stories, plus how to pick one together in minutes.",
    published: "2026-09-29",
    updated: "2026-09-29",
    answer:
      "The best feel-good movies are warm, funny and low-stress, with an ending that leaves you smiling: Paddington 2, The Princess Bride, Groundhog Day, About Time and School of Rock are classic picks. Below are 25 happy movies grouped by mood, from pure joy to comfort rewatches.",
    intro: [
      "Some nights you don't want to be challenged, shocked or left thinking for days. You want something that makes you feel better than when you started.",
      "That's what a feel-good movie does. The list below is grouped by the kind of lift you're after, so you can pick by mood instead of scrolling for half an hour.",
    ],
    sections: [
      {
        heading: "Pure joy",
        list: [
          "Paddington 2 (2017): kind, funny and beautifully made. Close to the perfect feel-good movie.",
          "The Princess Bride (1987): adventure, romance and jokes that work for every age.",
          "Singin' in the Rain (1952): the happiest musical ever made, and still a delight.",
          "Mamma Mia! (2008): ABBA songs, a Greek island and zero reasons to be sad.",
          "School of Rock (2003): Jack Black and a class of kids forming a band.",
        ],
      },
      {
        heading: "Comfort rewatches",
        list: [
          "Groundhog Day (1993): a grumpy weatherman relives the same day until he gets it right.",
          "Notting Hill (1999): a cozy romantic comedy that never gets old.",
          "Legally Blonde (2001): endlessly quotable and genuinely upbeat.",
          "Elf (2003): the go-to comfort movie in December, and honestly any other month.",
          "The Devil Wears Prada (2006): sharp, stylish and easy to watch again and again.",
        ],
      },
      {
        heading: "Heartwarming stories",
        list: [
          "Up (2009): an adventure about friendship and letting go. The first ten minutes may make you cry, the rest will make you smile.",
          "About Time (2013): a warm story about family and making the most of ordinary days.",
          "Little Miss Sunshine (2006): a chaotic family road trip that's funny and tender.",
          "CODA (2021): a teenager torn between her family and her dream of singing.",
          "Hidden Figures (2016): the true story of the women whose math got astronauts into space.",
        ],
      },
      {
        heading: "Funny and warm",
        list: [
          "Chef (2014): a chef starts over with a food truck. Don't watch it hungry.",
          "The Intern (2015): a gentle comedy about generations learning from each other.",
          "Hunt for the Wilderpeople (2016): a boy and his foster uncle on the run in the New Zealand bush.",
          "Crazy Rich Asians (2018): a romantic comedy with a big family at its heart.",
          "The Grand Budapest Hotel (2014): colorful, quirky and fast. Nothing else looks like it.",
        ],
      },
      {
        heading: "Uplifting and inspiring",
        list: [
          "The Secret Life of Walter Mitty (2013): a daydreamer finally goes on a real adventure.",
          "Sing Street (2016): a teenager starts a band in 1980s Dublin. The songs are great.",
          "Amélie (2001): a shy young woman secretly improves the lives of the people around her.",
          "Soul (2020): Pixar's thoughtful, uplifting take on what makes life worth living.",
          "Top Gun: Maverick (2022): pure crowd-pleasing fun with a big finish.",
        ],
      },
      {
        heading: "Feel-good shows if you want more than two hours",
        list: [
          "Ted Lasso: an American coach takes over an English soccer team, with kindness as his only tactic.",
          "Schitt's Creek: a rich family loses everything and slowly becomes lovable.",
          "Parks and Recreation: small-town government has never been this cheerful.",
        ],
      },
      {
        heading: "How to pick a comfort movie together",
        ordered: true,
        list: [
          "Agree on the mood first: laugh, cozy or inspired.",
          "Each person suggests one title from that group, then gets one veto.",
          "If you can't decide, pick the one somebody has already seen and loved. Rewatching is the point of a comfort movie.",
        ],
      },
      {
        heading: "Picking a feel-good movie with ReelMatch",
        paragraphs: [
          "In ReelMatch everyone swipes through trailers on their own phone, and the app shows the titles you all said yes to. A trailer is the fastest way to check a movie's mood before you commit. It's free on iPhone and Android.",
        ],
      },
    ],
    faqs: [
      {
        question: "What is the best feel-good movie of all time?",
        answer:
          "There's no single answer, but Paddington 2 (2017), The Princess Bride (1987), Singin' in the Rain (1952) and Groundhog Day (1993) appear on almost every list of the best feel-good movies.",
      },
      {
        question: "What are good comfort movies?",
        answer:
          "Comfort movies are ones you can rewatch without effort: Notting Hill (1999), Legally Blonde (2001), Elf (2003), The Devil Wears Prada (2006) and Mamma Mia! (2008) are popular choices.",
      },
      {
        question: "What movies should I watch when I'm sad?",
        answer:
          "Choose something warm and low-stress with a happy ending, such as Paddington 2 (2017), About Time (2013), Chef (2014) or School of Rock (2003). Watching with someone else usually helps too.",
      },
      {
        question: "What are good feel-good family movies?",
        answer:
          "Paddington 2 (2017), Up (2009), The Princess Bride (1987) and School of Rock (2003) work for most ages. See our family movies guide for picks organized by age.",
      },
      {
        question: "Is there an app to help pick a feel-good movie?",
        answer:
          "Yes. In ReelMatch each person swipes through trailers on their own phone and the app shows the titles everyone said yes to. It's free on iPhone and Android.",
      },
    ],
    related: [
      "family-christmas-movies",
      "movies-to-watch-as-a-couple",
      "family-movies-to-watch",
      "movies-to-watch-with-your-mom",
    ],
  },
  {
    slug: "family-christmas-movies",
    title: "Family Christmas movies: 30 classic, funny and animated picks for every age",
    metaTitle: "Family Christmas Movies: 30 Classic, Funny & Animated Picks",
    description:
      "30 family Christmas movies for every age: timeless classics, funny favorites, animated picks for kids and a few for the grown-ups, plus how to choose one.",
    published: "2026-09-29",
    updated: "2026-09-29",
    answer:
      "The best family Christmas movies work for kids and adults alike: Elf, Home Alone, The Polar Express, Klaus and It's a Wonderful Life are safe picks. Start from the youngest viewer, let everyone suggest one title and use the trailers to decide. Below are 30 Christmas movies grouped by type.",
    intro: [
      "December is the one month when almost every family wants to watch something together. The trouble is that everyone has a different favorite, and the youngest and oldest viewers rarely want the same thing.",
      "This list is grouped by type, from timeless classics to animated picks for little kids and a few for the grown-ups once the kids are in bed.",
    ],
    sections: [
      {
        heading: "Timeless Christmas classics",
        list: [
          "It's a Wonderful Life (1946): the classic about how much one life matters. Still moving.",
          "Miracle on 34th Street (1947): a department-store Santa who might be the real thing.",
          "White Christmas (1954): songs, dancing and snow. A cozy musical for the whole family.",
          "A Christmas Story (1983): one boy's quest for a BB gun, told with dry humor.",
          "Home Alone (1990, PG): the most rewatched family Christmas movie there is.",
          "The Muppet Christmas Carol (1992): the best version of the Dickens story for kids, and many adults agree.",
        ],
      },
      {
        heading: "Funny Christmas movies",
        list: [
          "Elf (2003, PG): Will Ferrell as a human raised by elves. Pure joy.",
          "Home Alone 2: Lost in New York (1992, PG): the same fun, this time in New York.",
          "The Santa Clause (1994, PG): a dad accidentally becomes Santa.",
          "National Lampoon's Christmas Vacation (1989, PG-13): every family holiday disaster in one movie. Best for older kids.",
          "Jingle All the Way (1996, PG): two dads fighting over the year's must-have toy.",
          "Arthur Christmas (2011, PG): how Santa's family really delivers all those presents.",
        ],
      },
      {
        heading: "Animated Christmas movies for kids",
        list: [
          "Klaus (2019, PG): a beautifully animated origin story for Santa. A modern classic.",
          "The Polar Express (2004, G): a magical train ride to the North Pole.",
          "The Grinch (2018, PG): a bright, gentle version of the Dr. Seuss story for younger kids.",
          "How the Grinch Stole Christmas! (1966): the original 26-minute special, perfect before bedtime.",
          "A Charlie Brown Christmas (1965): short, sweet and full of great music.",
          "The Nightmare Before Christmas (1993, PG): works for both Halloween and December.",
        ],
      },
      {
        heading: "Romantic Christmas movies",
        list: [
          "The Holiday (2006, PG-13): two women swap homes for the holidays. The ultimate cozy rewatch.",
          "Love Actually (2003, R): several love stories in the weeks before Christmas in London.",
          "While You Were Sleeping (1995, PG): a sweet, funny romance set around Christmas in Chicago.",
          "Last Christmas (2019, PG-13): a London Christmas romance full of George Michael songs.",
          "Happiest Season (2020, PG-13): a holiday romantic comedy about meeting the family.",
          "Carol (2015, R): an elegant 1950s love story set around Christmas in New York.",
        ],
      },
      {
        heading: "Christmas movies for adults",
        list: [
          "Die Hard (1988, R): the eternal debate about whether it's a Christmas movie. It is.",
          "Scrooged (1988, PG-13): Bill Murray in a sharp, modern take on A Christmas Carol.",
          "The Family Stone (2005, PG-13): a messy, funny and emotional family Christmas.",
          "Gremlins (1984, PG): a creepy-funny Christmas classic. Too scary for little kids.",
          "The Night Before (2015, R): three old friends' last big Christmas Eve out in New York.",
          "Tokyo Godfathers (2003, PG-13): an anime about three unlikely friends who find a baby on Christmas Eve.",
        ],
      },
      {
        heading: "Pick by age",
        list: [
          "Under 6: A Charlie Brown Christmas, How the Grinch Stole Christmas! (1966), The Grinch (2018).",
          "6 to 10: Elf, The Polar Express, Arthur Christmas, The Santa Clause, Klaus.",
          "10 and up: Home Alone, A Christmas Story, The Muppet Christmas Carol, Christmas Vacation.",
          "Teens and adults: The Holiday, Die Hard, Scrooged, The Family Stone.",
        ],
      },
      {
        heading: "How to choose a Christmas movie as a family",
        ordered: true,
        list: [
          "Start from the youngest viewer's age.",
          "Everyone suggests one title, and everyone gets one veto.",
          "Make a December list and cross movies off as you go, so nobody has to choose from scratch every night.",
        ],
      },
      {
        heading: "Choosing together with ReelMatch",
        paragraphs: [
          "In ReelMatch each person swipes through trailers on their own phone, and the app shows the titles everyone said yes to. It's a quick way to build a family Christmas watch list that nobody argues about. It's free on iPhone and Android.",
        ],
      },
    ],
    faqs: [
      {
        question: "What are the best family Christmas movies?",
        answer:
          "Elf (2003), Home Alone (1990), The Polar Express (2004), Klaus (2019), The Muppet Christmas Carol (1992) and It's a Wonderful Life (1946) are among the most loved family Christmas movies.",
      },
      {
        question: "What are the funniest Christmas movies?",
        answer:
          "Elf (2003), Home Alone (1990), National Lampoon's Christmas Vacation (1989), The Santa Clause (1994) and Arthur Christmas (2011) are the go-to funny Christmas movies.",
      },
      {
        question: "What are good animated Christmas movies?",
        answer:
          "Klaus (2019), The Polar Express (2004), The Grinch (2018), Arthur Christmas (2011) and the classic specials How the Grinch Stole Christmas! (1966) and A Charlie Brown Christmas (1965).",
      },
      {
        question: "What are good romantic Christmas movies?",
        answer:
          "The Holiday (2006), Love Actually (2003), While You Were Sleeping (1995), Last Christmas (2019) and Happiest Season (2020).",
      },
      {
        question: "Is there an app to help a family pick a Christmas movie?",
        answer:
          "Yes. In ReelMatch each person swipes through trailers on their own phone and the app shows the titles everyone said yes to. It's free on iPhone and Android.",
      },
    ],
    related: ["thanksgiving-movies", "family-movies-to-watch", "feel-good-movies"],
  },

  {
    slug: "thanksgiving-movies",
    title: "Thanksgiving movies to watch with family (plus picks for the long weekend)",
    metaTitle: "Thanksgiving Movies to Watch With Family: 20 Picks",
    description:
      "20 Thanksgiving movies to watch with family, from Planes, Trains and Automobiles to A Charlie Brown Thanksgiving, plus picks for the long holiday weekend.",
    published: "2026-09-29",
    updated: "2026-09-29",
    answer:
      "The best Thanksgiving movies are Planes, Trains and Automobiles, Home for the Holidays, A Charlie Brown Thanksgiving, Addams Family Values and Miracle on 34th Street. After dinner, pick something the whole table can enjoy, and save the Christmas movies to kick off the season on Friday.",
    intro: [
      "After the turkey, the pie and the dishes, a movie is the easiest way to keep everyone together on the couch. The trick is finding one that works for grandparents, parents and kids at the same time.",
      "There are fewer Thanksgiving movies than Christmas movies, but the good ones are very good. Below are the best Thanksgiving-set movies, plus family picks for the rest of the long weekend.",
    ],
    sections: [
      {
        heading: "Best Thanksgiving movies",
        list: [
          "Planes, Trains and Automobiles (1987, R): Steve Martin and John Candy trying to get home for Thanksgiving. The classic.",
          "Home for the Holidays (1995, PG-13): a chaotic, funny and very recognizable family Thanksgiving.",
          "Pieces of April (2003, PG-13): a daughter tries to cook Thanksgiving dinner for the family she's estranged from.",
          "The Humans (2021, R): one family's tense Thanksgiving dinner in a New York apartment.",
          "Dutch (1991, PG-13): a road-trip comedy about getting a kid home for Thanksgiving.",
          "Scent of a Woman (1992, R): Al Pacino and a Thanksgiving weekend in New York.",
        ],
      },
      {
        heading: "Thanksgiving movies for kids and families",
        list: [
          "A Charlie Brown Thanksgiving (1973): a 25-minute tradition for families with young kids.",
          "Miracle on 34th Street (1947): it opens at the Macy's Thanksgiving Day Parade and ends at Christmas.",
          "Addams Family Values (1993, PG-13): the summer-camp Thanksgiving play is one of the funniest scenes of the '90s.",
          "Free Birds (2013, PG): two turkeys travel back in time to take turkey off the menu.",
          "The Blind Side (2009, PG-13): a true story with a Thanksgiving dinner scene that gets everyone.",
        ],
      },
      {
        heading: "Something different",
        list: [
          "Rocky (1976, PG): the film's most tender scene happens on Thanksgiving.",
          "The Ice Storm (1997, R): a dark family drama set over Thanksgiving weekend. For adults only.",
          "Thanksgiving (2023, R): a slasher set around Black Friday. Only for horror fans.",
        ],
      },
      {
        heading: "Kick off the holiday season on Friday",
        paragraphs: [
          "Plenty of families start Christmas movies the day after Thanksgiving. These work for every age:",
        ],
        list: [
          "Elf (2003, PG)",
          "Home Alone (1990, PG)",
          "The Polar Express (2004, G)",
          "Klaus (2019, PG)",
          "Paddington 2 (2017, PG): not a holiday movie, but warm enough to feel like one.",
          "Knives Out (2019, PG-13): a family whodunit that's fun after a big family dinner.",
        ],
      },
      {
        heading: "How to pick a movie with the whole family",
        ordered: true,
        list: [
          "Start from the youngest person watching.",
          "Keep it under two hours. After a big dinner, attention spans are short.",
          "Everyone suggests one title, everyone gets one veto, and the trailers settle it.",
        ],
      },
      {
        heading: "Choosing together with ReelMatch",
        paragraphs: [
          "In ReelMatch each person swipes through trailers on their own phone, and the app shows the titles everyone said yes to. Start before dinner and the movie is already chosen by dessert. It's free on iPhone and Android.",
        ],
      },
    ],
    faqs: [
      {
        question: "What is the best Thanksgiving movie?",
        answer:
          "Planes, Trains and Automobiles (1987) is usually called the best Thanksgiving movie. Home for the Holidays (1995) and A Charlie Brown Thanksgiving (1973) are other favorites.",
      },
      {
        question: "What are good Thanksgiving movies for kids?",
        answer:
          "A Charlie Brown Thanksgiving (1973), Free Birds (2013) and Miracle on 34th Street (1947) work for young kids. For older kids, try Addams Family Values (1993).",
      },
      {
        question: "What should we watch after Thanksgiving dinner?",
        answer:
          "Pick something short and easy for every age, such as Elf (2003), Paddington 2 (2017) or Knives Out (2019). Many families start Christmas movies that weekend.",
      },
      {
        question: "Is there an app to help a family pick a movie?",
        answer:
          "Yes. In ReelMatch each person swipes through trailers on their own phone and the app shows the titles everyone said yes to. It's free on iPhone and Android.",
      },
    ],
    related: ["family-christmas-movies", "family-movies-to-watch", "movies-to-watch-with-your-mom"],
  },
];
